import { memo, useMemo } from 'react'

import { DirectionsRun } from '@mui/icons-material'
import { Box, LinearProgress, Paper, Typography } from '@mui/material'
import { SmepEvent } from '@nativewaves/nw-cloud-sdk/smep'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { DATE_TIME_FORMAT } from '@shared/utils/support'

type EventProgressProps = {
  event: SmepEvent
}

const EventProgressRaw = ({ event }: EventProgressProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const hasStarted = event.status?.start && Date.now() - event.status?.start * 1000 > 0
  const openEnd = !event.endTime
  const hasEnded = event.status?.ended
  const completeDuration =
    !!event.endTime && !!event.startTime && new Date(event.endTime).valueOf() - new Date(event.startTime).valueOf()

  const startTimeFormatted = useMemo(
    () => (event.startTime ? format(new Date(event.startTime), DATE_TIME_FORMAT) : undefined),
    [event.startTime],
  )

  const endTimeFormatted = useMemo(
    () => (event.endTime ? format(new Date(event.endTime), DATE_TIME_FORMAT) : undefined),
    [event.endTime],
  )

  return (
    <Paper sx={{ width: '100%', borderRadius: 2, py: 3, px: 4 }} elevation={2}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="heading-md">{t('domain:Workspace.Event.eventProgress')}</Typography>
        <DirectionsRun />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ mb: 1 }}>
          <Typography sx={{ display: 'block' }} variant="caption">
            {t('domain:Workspace.Event.start')}
          </Typography>
          <Typography variant="heading-sm">{startTimeFormatted}</Typography>
        </Box>

        <Box sx={{ mb: 1, textAlign: 'end' }}>
          <Typography sx={{ display: 'block' }} variant="caption">
            {t('domain:Workspace.Event.end')}
          </Typography>
          <Typography variant="heading-sm">{endTimeFormatted ?? 'Open end'}</Typography>
        </Box>
      </Box>

      <LinearProgress
        variant="buffer"
        value={
          hasEnded
            ? 100
            : hasStarted
              ? openEnd
                ? 50
                : completeDuration && event.status?.duration
                  ? Math.floor(((event.status.duration * 1000) / completeDuration) * 100)
                  : 0
              : 0
        }
        valueBuffer={
          hasStarted && completeDuration && event.status?.duration
            ? Math.floor(((event.status.duration * 1000) / completeDuration) * 100)
            : openEnd
              ? 60
              : 100
        }
        // An event that is over is history, not a success state — grey the bar out.
        color={hasEnded ? 'inherit' : 'info'}
        sx={hasEnded ? { color: 'text.disabled' } : undefined}
      />
    </Paper>
  )
}

export const EventProgress = memo(EventProgressRaw)
